import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { emptyGuid } from '../../../util';
import { IOrderFilterOptions } from '../Components/OrderFilterDialog';

export interface UseOrderListFiltersOptions {
    /** When true, read/write `customerId` and `customerLabel` in the URL (global orders page). */
    includeCustomer: boolean;
}

function parseOrderFiltersFromUrl(search: string, includeCustomer: boolean): IOrderFilterOptions {
    const params = new URLSearchParams(search);
    const orderDateRaw = params.get('orderDate');
    let orderDate: Date | undefined;
    if (orderDateRaw) {
        const parsed = new Date(orderDateRaw);
        if (!Number.isNaN(parsed.getTime())) {
            orderDate = parsed;
        }
    }

    const materialIdParam = params.get('materialId');
    const materialId =
        materialIdParam && materialIdParam !== emptyGuid ? materialIdParam : emptyGuid;
    const materialLabel = materialId !== emptyGuid ? params.get('materialLabel') ?? '' : '';

    let customerId = emptyGuid;
    let customerLabel = '';
    if (includeCustomer) {
        const cid = params.get('customerId');
        if (cid && cid !== emptyGuid) {
            customerId = cid;
            customerLabel = params.get('customerLabel') ?? '';
        }
    }

    return {
        customerId,
        customerLabel,
        materialId,
        materialLabel,
        orderDate,
    };
}

function applyOrderFiltersToSearchParams(
    prev: URLSearchParams,
    filters: IOrderFilterOptions,
    includeCustomer: boolean
): URLSearchParams {
    const next = new URLSearchParams(prev);

    if (includeCustomer) {
        if (filters.customerId !== emptyGuid) {
            next.set('customerId', filters.customerId);
            next.set('customerLabel', filters.customerLabel);
        } else {
            next.delete('customerId');
            next.delete('customerLabel');
        }
    } else {
        next.delete('customerId');
        next.delete('customerLabel');
    }

    if (filters.materialId !== emptyGuid) {
        next.set('materialId', filters.materialId);
        next.set('materialLabel', filters.materialLabel);
    } else {
        next.delete('materialId');
        next.delete('materialLabel');
    }

    if (filters.orderDate) {
        next.set('orderDate', filters.orderDate.toISOString());
    } else {
        next.delete('orderDate');
    }

    return next;
}

/**
 * Keeps order list filters in sync with URL query params (bookmarkable), matching Quotes / Material Price.
 * Uses functional `setSearchParams` updates so repeated keys (e.g. multi-column `sorting`) stay intact.
 */
export function useOrderListFilters(options: UseOrderListFiltersOptions): {
    orderFilters: IOrderFilterOptions;
    setOrderFilters: Dispatch<SetStateAction<IOrderFilterOptions>>;
} {
    const { includeCustomer } = options;
    const [, setSearchParams] = useSearchParams();

    const [orderFilters, setOrderFilters] = useState<IOrderFilterOptions>(() =>
        parseOrderFiltersFromUrl(typeof window !== 'undefined' ? window.location.search : '', includeCustomer)
    );

    useEffect(() => {
        setSearchParams((prev) => applyOrderFiltersToSearchParams(prev, orderFilters, includeCustomer));
    }, [orderFilters, includeCustomer, setSearchParams]);

    return { orderFilters, setOrderFilters };
}
